import { useNavigate } from "react-router-dom";
import { NuplayWordmark } from "./components/NuplayWordmark";

// KnitWell / Ann Taylor WISMO demo landing. Minimal by design — one live agent
// (Ava), one seeded caller (John Mike). Deep-links into the shared call surface
// at /experience?agent=knitwell.

const JOHN_MIKE = "/nurix-experience/experience?agent=knitwell&persona=knit_JOHNMIKE&query=wismo&channel=web";

// Ann Taylor brand mark — elegant black tile with a serif "AT".
function AnnTaylorMark({ size = 34 }: { size?: number }) {
  return (
    <span
      className="flex items-center justify-center text-white"
      style={{ width: size, height: size, borderRadius: 8, background: "#111111", fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 600, fontSize: size * 0.42, letterSpacing: 0.5 }}
    >
      AT
    </span>
  );
}

const FEATURES = [
  {
    gradient: "from-[#2582EB] to-[#1D6BD8]",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    title: "Where is my order?",
    desc: "Ava looks up the caller's recent orders and reads back the live shipment status — carrier, last scan, and a fresh ETA.",
  },
  {
    gradient: "from-[#34d399] to-[#059669]",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12l2 2 4-4" /><path d="M12 3a9 9 0 1 0 9 9" />
      </svg>
    ),
    title: "Verifies the caller first",
    desc: "Confirms who's on the line before sharing any order details — natural, conversational, no clunky menus.",
  },
  {
    gradient: "from-[#fbbf24] to-[#f59e0b]",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
    title: "Handles multiple orders",
    desc: "Five recent orders on file — Ava finds the right one, tracks it, and keeps the conversation on your order.",
  },
];

export default function KnitwellLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--nx-bg)", fontFamily: "Nunito, system-ui, sans-serif" }}>
      {/* ── Background atmosphere ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="orb animate-orb_float" style={{ width: 700, height: 700, top: "-15%", right: "-10%", background: "radial-gradient(circle, rgba(37,130,235,0.13) 0%, transparent 70%)" }} />
        <div className="orb" style={{ width: 500, height: 500, bottom: "5%", left: "-8%", background: "radial-gradient(circle, rgba(37,130,235,0.09) 0%, transparent 70%)", animationDelay: "3s" }} />
        <div className="absolute inset-0 opacity-[0.018]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      </div>

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-30 flex-shrink-0" style={{ background: "var(--nx-nav)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AnnTaylorMark />
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold tracking-tight" style={{ color: "var(--nx-text-1)" }}>Ann Taylor</span>
              <span className="text-[9px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--nx-text-3)" }}>Ava · Order Support</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-[11px] font-medium" style={{ color: "var(--nx-text-3)" }}>Powered by</span>
              <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={15} /></span>
            </div>
            <span className="hidden sm:block w-px h-5" style={{ background: "var(--nx-border)" }} />
            <button
              onClick={() => navigate(JOHN_MIKE)}
              className="flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-medium transition-all duration-150 hover:scale-105"
              style={{ background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.20)", color: "var(--color-success)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--color-success)" }} />
              AI Agent Online
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div className="relative flex-1">
        <div className="relative max-w-6xl mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20 grid md:grid-cols-[1fr_420px] gap-14 items-start">
          {/* Left — copy */}
          <div className="step-enter">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.09em] uppercase mb-8" style={{ background: "rgba(37,130,235,0.12)", border: "1px solid rgba(37,130,235,0.25)", color: "var(--nx-accent-text)" }}>
              <span className="w-1 h-1 rounded-full" style={{ background: "var(--nx-accent-text)" }} />
              Order Tracking Demo
            </div>
            <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.08] tracking-[-0.035em] mb-6" style={{ color: "var(--nx-text-1)" }}>
              “Where is my order?” —{" "}
              <span style={{ background: "linear-gradient(135deg, #60BDFA 0%, #2582EB 50%, #1D4885 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                answered live.
              </span>
            </h1>
            <p className="text-[16px] leading-relaxed mb-12 max-w-lg" style={{ color: "var(--nx-text-2)" }}>
              Meet <span className="font-semibold" style={{ color: "var(--nx-text-1)" }}>Ava</span>, Ann Taylor’s AI order-tracking associate. A real call, a seeded test customer with five recent orders — verify, look up, and track the shipment end to end.
            </p>
            <div className="space-y-6">
              {FEATURES.map(({ gradient, icon, title, desc }) => (
                <div key={title} className="flex items-start gap-4">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg`}>{icon}</div>
                  <div>
                    <div className="text-[14px] font-semibold mb-0.5" style={{ color: "var(--nx-text-1)" }}>{title}</div>
                    <div className="text-[13px] leading-relaxed" style={{ color: "var(--nx-text-2)" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — demo card */}
          <div className="step-enter" style={{ animationDelay: "60ms" }}>
            <div className="rounded-2xl overflow-hidden" style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)", boxShadow: "0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px var(--nx-border)", backdropFilter: "blur(20px)" }}>
              <div className="h-[3px]" style={{ background: "linear-gradient(90deg, #2582EB 0%, #60BDFA 50%, #34d399 100%)" }} />
              <div className="px-7 pt-7 pb-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #2582EB, #1D6BD8)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="22" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-[15px] font-bold" style={{ color: "var(--nx-text-1)" }}>Ava — Order Tracking</div>
                    <div className="text-[12px]" style={{ color: "var(--nx-text-3)" }}>Ann Taylor</div>
                  </div>
                </div>
                <p className="text-[13px] leading-relaxed mb-6" style={{ color: "var(--nx-text-2)" }}>
                  Start a live call as <span className="font-semibold" style={{ color: "var(--nx-text-1)" }}>John Mike</span> and watch Ava verify the caller, then look up and track his recent orders. No account needed.
                </p>
                <div className="rounded-xl p-4 mb-7" style={{ background: "var(--nx-hover)", border: "1px solid var(--nx-border)" }}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.09em] mb-2.5" style={{ color: "var(--nx-text-3)" }}>Test customer</div>
                  <div className="flex items-center justify-between text-[12.5px]" style={{ color: "var(--nx-text-2)" }}>
                    <span>Name</span><span className="font-semibold" style={{ color: "var(--nx-text-1)" }}>John Mike</span>
                  </div>
                  <div className="flex items-center justify-between text-[12.5px] mt-1.5" style={{ color: "var(--nx-text-2)" }}>
                    <span>Phone</span><span className="font-semibold tabular-nums" style={{ color: "var(--nx-text-1)" }}>615 767 5101</span>
                  </div>
                  <div className="flex items-center justify-between text-[12.5px] mt-1.5" style={{ color: "var(--nx-text-2)" }}>
                    <span>Recent orders</span><span className="font-semibold tabular-nums" style={{ color: "var(--nx-text-1)" }}>5 on file</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(JOHN_MIKE)}
                  className="w-full h-12 rounded-xl flex items-center justify-center gap-2.5 text-[14px] font-semibold text-white transition-all duration-150 active:scale-[0.985] relative overflow-hidden"
                  style={{ background: "linear-gradient(135deg, #2582EB 0%, #1D6BD8 100%)", boxShadow: "0 4px 24px rgba(37,130,235,0.4)" }}
                >
                  Start the Demo
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                <p className="text-center text-[11px] mt-3.5" style={{ color: "var(--nx-text-3)" }}>
                  No account required · Live WISMO call
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={{ borderTop: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AnnTaylorMark size={22} />
            <span className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>Ann Taylor — order-tracking demo</span>
          </div>
          <span className="flex items-center gap-2 text-[13px]" style={{ color: "var(--nx-text-3)" }}>
            Powered by
            <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={16} /></span>
          </span>
        </div>
      </footer>
    </div>
  );
}
